import { useCallback, useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";

type RowRectMap = Map<string, DOMRect>;

function shouldReduceMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readRowRects(container: HTMLElement | null): RowRectMap {
  const rects: RowRectMap = new Map();
  container
    ?.querySelectorAll<HTMLElement>("[data-nurse-row-id]")
    .forEach((row) => {
      const rowId = row.dataset.nurseRowId;
      if (!rowId) return;
      rects.set(rowId, row.getBoundingClientRect());
    });
  return rects;
}

export function useRowSwapFlipAnimation(
  containerRef: React.RefObject<HTMLElement | null>,
  rowIds: readonly string[]
): () => void {
  const previousRectsRef = useRef<RowRectMap | null>(null);

  const captureRowPositions = useCallback(() => {
    if (shouldReduceMotion()) return;
    previousRectsRef.current = readRowRects(containerRef.current);
  }, [containerRef]);

  useLayoutEffect(() => {
    const previousRects = previousRectsRef.current;
    if (!previousRects || shouldReduceMotion()) return;
    previousRectsRef.current = null;

    containerRef.current
      ?.querySelectorAll<HTMLElement>("[data-nurse-row-id]")
      .forEach((row) => {
        const rowId = row.dataset.nurseRowId;
        if (!rowId) return;
        const previous = previousRects.get(rowId);
        if (!previous) return;
        const current = row.getBoundingClientRect();
        const deltaY = previous.top - current.top;
        if (Math.abs(deltaY) < 1) return;

        gsap.fromTo(
          row,
          { y: deltaY },
          {
            y: 0,
            duration: 0.18,
            ease: "power2.out",
            overwrite: true,
          }
        );
      });
  }, [containerRef, rowIds]);

  return captureRowPositions;
}
